import { useEffect, useRef } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useChatPageStore } from '../store';
import { SubContentType } from '../types';
import { getInsightChipPost } from '@/services/swagger/easourcing/SessionService';

/** 轮询间隔（毫秒） */
const POLL_INTERVAL = 3000;
/** 最大轮询次数（约 60s 超时） */
const MAX_POLL_COUNT = 20;

/**
 * useInsightChipPolling
 *
 * 副作用 Hook：监听 msgList 的最后一条消息，当其 subMessage 列表中存在
 * INQUIRY_CARD 类型且尚未存在 INSIGHT_CHIP 类型时，自动轮询
 * /api/biz/session/insight-chip 接口。接口返回 subMessageId 不为空时，
 * 将 INSIGHT_CHIP sub message 插入到该消息的 subMessage 列表并停止轮询。
 *
 * 轮询终止条件：
 * 1. 接口返回有效 subMessageId（成功）
 * 2. 达到最大轮询次数 MAX_POLL_COUNT（超时，静默失败）
 * 3. 最后一条消息变化，不再满足前置条件（停止旧轮询）
 * 4. 组件卸载（离页清理）
 */
export function useInsightChipPolling() {
  const sessionId = useChatPageStore(s => s.sessionId);
  const msgList = useChatPageStore(s => s.msgList);
  const insertInsightChipSubMsg = useChatPageStore(s => s.insertInsightChipSubMsg);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const isRequestingRef = useRef(false);

  /** 停止并清理当前轮询 */
  const stopPolling = useMemoizedFn(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    pollCountRef.current = 0;
    isRequestingRef.current = false;
  });

  useEffect(() => {
    // 1. 取最后一条消息
    const lastMsg = msgList[msgList.length - 1];
    if (!lastMsg) {
      stopPolling();
      return;
    }

    const subMessages = lastMsg.subMessage ?? [];

    // 2. 检查前置条件：最后一条消息含 INQUIRY_CARD 类型 sub message
    const inviteCard = subMessages.find(s => s.contentType === SubContentType.inquiryCard);
    if (!inviteCard) {
      stopPolling();
      return;
    }

    // 3. 幂等保护：已存在 INSIGHT_CHIP 则无需轮询
    const alreadyHasChip = subMessages.some(s => s.contentType === SubContentType.insightChip);
    if (alreadyHasChip) {
      stopPolling();
      return;
    }

    // 4. 前置条件满足，启动轮询
    const msgId = lastMsg.messageId;

    // 清理上一轮残留（msgList 变化时重置）
    stopPolling();
    pollCountRef.current = 0;

    timerRef.current = setInterval(async () => {
      // 超出最大次数，静默停止
      if (pollCountRef.current >= MAX_POLL_COUNT) {
        stopPolling();
        return;
      }

      // 防止上次请求未完成时重复发起
      if (isRequestingRef.current) return;

      isRequestingRef.current = true;
      pollCountRef.current += 1;

      try {
        const result = await getInsightChipPost(
          { messageId: String(msgId) },
          // 跳过全局错误处理，避免弹出 toast，由轮询自行处理（静默失败）
          { skipGlobalErrorHandling: true } as Record<string, unknown>
        );

        const subMsg = result?.data;
        if (subMsg?.subMessageId) {
          insertInsightChipSubMsg(lastMsg.messageId, subMsg);
          stopPolling();
        }
      } catch {
        // 网络错误：计数已 +1，继续轮询直到上限（静默失败，不展示任何 UI 反馈）
      } finally {
        isRequestingRef.current = false;
      }
    }, POLL_INTERVAL);

    // cleanup：msgList 依赖变化或组件卸载时清理计时器
    return () => {
      stopPolling();
    };
  }, [msgList, sessionId, insertInsightChipSubMsg, stopPolling]);
}
